/**
 * Sounds.txt as the engine sees it. Every sound is a row; other tables (SoundEnviron, item and
 * monster sounds) refer to rows by index. In LoD 1.07 the rows are named by a `Handle` column
 * (`ESOUND_CURSOR_PASS`); later patches renamed it `Sound` with lowercase names, so lookups accept
 * either spelling.
 *
 * A row with `Group Size` N > 1 heads a group: the engine picks one of the N consecutive rows at
 * random each time (e.g. EVENT_CAVE_1 with N = 8 covers drip1..drip8).
 */
import type { ExcelTable } from '@d2/formats';

export interface SoundDef {
  index: number;
  handle: string;
  /** Archive path of the WAV (music rows resolve under data\global\music). */
  file: string;
  /** 0..1 (column 0..255). */
  volume: number;
  groupSize: number;
  loop: boolean;
  /** Fades in game frames (1/25 s). */
  fadeIn: number;
  fadeOut: number;
  /** Don't start while an instance of this sound is still playing. */
  deferInst: boolean;
  /** A new instance stops the previous one. */
  stopInst: boolean;
  /** Distance class: 0 none, 1 normal, 2 ambient event, 3 thunder, 4 speech (see spatial.ts). */
  falloff: number;
  priority: number;
  stream: boolean;
}

const SFX_ROOT = 'data\\global\\sfx\\';
const MUSIC_ROOT = 'data\\global\\music\\';
/** Localised speech (the speech falloff class) ships under data\local. */
const SPEECH_ROOT = 'data\\local\\sfx\\';
const SPEECH_FALLOFF = 4;
/** The table's placeholder file for "no sound". */
const SILENT = 'none.wav';

function normName(n: string): string {
  return n.toLowerCase().replace(/^esound_/, '');
}

export class SoundTable {
  readonly rows: (SoundDef | undefined)[] = [];
  private readonly byName = new Map<string, number>();

  constructor(t: ExcelTable) {
    const nameCol = t.has('Handle') ? 'Handle' : 'Sound';
    for (let pos = 0; pos < t.length; pos++) {
      const r = t.row(pos);
      const handle = r.str(nameCol);
      // Other tables refer to rows by the Index column, which matches the row position in the
      // shipped tables; prefer it in case separator rows were skipped.
      const i = r.int('Index', pos);
      const file = r.str('FileName').replace(/\//g, '\\');
      if (!handle) continue;
      // Songs live in the music folder and voices in the localised one; the table lists files
      // relative to those.
      const falloff = r.int('Falloff', 1);
      const root = /^(esound_)?music_/i.test(handle) ? MUSIC_ROOT : falloff === SPEECH_FALLOFF ? SPEECH_ROOT : SFX_ROOT;
      this.rows[i] = {
        index: i,
        handle,
        file: !file || file.toLowerCase() === SILENT ? '' : root + file,
        volume: r.int('Volume', 255) / 255,
        groupSize: r.int('Group Size', 0),
        loop: r.bool('Loop'),
        fadeIn: r.int('Fade In'),
        fadeOut: r.int('Fade Out'),
        deferInst: r.bool('Defer Inst'),
        stopInst: r.bool('Stop Inst'),
        falloff,
        priority: r.int('Priority', 100),
        stream: r.bool('Stream'),
      };
      this.byName.set(normName(handle), i);
    }
  }

  get(index: number): SoundDef | undefined {
    return index > 0 ? this.rows[index] : undefined;
  }

  /**
   * Finds a row by name, accepting `ESOUND_X`, `x`, and a group's base name (`sorceress_needmana`
   * finds `SORCERESS_NEEDMANA_1`).
   */
  find(name: string): SoundDef | undefined {
    const n = normName(name);
    const i = this.byName.get(n) ?? this.byName.get(`${n}_1`);
    return i === undefined ? undefined : this.rows[i];
  }

  /** The row actually played for `def`: a random member of its group, if it heads one. */
  pick(def: SoundDef, rand: () => number = Math.random): SoundDef {
    if (def.groupSize <= 1) return def;
    return this.rows[def.index + Math.floor(rand() * def.groupSize)] ?? def;
  }

  /** Every row `def` may play (the group members, or itself). */
  members(def: SoundDef): SoundDef[] {
    if (def.groupSize <= 1) return [def];
    const out: SoundDef[] = [];
    for (let i = 0; i < def.groupSize; i++) {
      const m = this.rows[def.index + i];
      if (m) out.push(m);
    }
    return out;
  }
}
